import {useCallback, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {rxUpdateTransactions} from "./slices/transactionsSlice";
import {rxToggleLeftMenu, rxUpdateRates, rxUpdateSettings} from "./slices/appSlice";
import {initializeApp} from "firebase/app";
import {collection, getFirestore, onSnapshot, orderBy, query, where} from "firebase/firestore";
import {rxUpdateAccounts} from "./slices/accountsSlice";
import {rxUpdateCategories} from "./slices/categoriesSlice";
import {rxUpdateRules} from "./slices/rulesSlice";
import {rxUpdateTags} from "./slices/tagsSlice";
import {rxUpdateCars} from "./slices/carsSlice";
import {rxUpdateMileageLogs} from "./slices/mileageLogsSlice";
import {useNavigate} from "react-router";
import {dbDelete} from "./util/db-util";
import {rxUpdatePortfolioTransactions} from "./slices/portfolioSlice";
import {rxUpdateQuotes} from "./slices/quotesSlice";
import {rxUpdateBudgetCategories} from "./slices/budgetCategoriesSlice";
import {getQuotes} from "./api/ApiQuotes";
import useAppInit from "./useAppInit";
import useFirebase from "./util/useFirebase";

const FIREBASE_CONFIG = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_MESSAGING_APP_ID
};

const FIREBASE_APP = initializeApp(FIREBASE_CONFIG);

let signingOut = false;
let firestoreSubscriptions = [];

let locale, currencyMain;

const useApp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {getAuth, onAuthStateChanged, signOut} = useFirebase();

  const {init} = useAppInit();

  const firestoreDb = getFirestore(FIREBASE_APP);

  const initialized = useSelector(state => state.app.initialized);

  const toggleLeftMenu = useCallback(() => dispatch(rxToggleLeftMenu()), [dispatch]);

  const closeLeftMenuMobile = useCallback(() => {
    if (window.innerWidth < 1500) {
      toggleLeftMenu();
    }
  }, [toggleLeftMenu]);

  const logout = () => {
    signingOut = true;

    return signOut(getAuth()).then(() => {
      firestoreSubscriptions.forEach(u => u());

      dbDelete();

      window.location.href = "/signin";
    });
  };

  const doSyncQuotes = useCallback(() => {
    return getQuotes().then(result => {
      dispatch(rxUpdateQuotes({uquotes: result.quotes}));
      dispatch(rxUpdateRates(result.rates));
    });
  }, [dispatch])

  const syncQuotes = useCallback(() => {
    setTimeout(() => {
      doSyncQuotes().then(syncQuotes);
    }, 5 * 60 * 1000);
  }, [doSyncQuotes]);

  const listenFirestoreChange = useCallback(projectId => {
    const makeQuery = path => {
      return query(
        collection(firestoreDb, path),
        where("projectId", "==", projectId),
        where("time", ">", new Date().getTime()),
        orderBy("time", "asc")
      );
    };

    const listenChanges = (query, action) => {
      firestoreSubscriptions.push(onSnapshot(query, snapshot => {
        const items = snapshot.docChanges().map(change => change.doc.data().payload);

        if (items.length > 0) {
          dispatch(action(items));
        }
      }));
    };

    const listentUpdateDeleteChanges = (query, action, updateKey = "u", deleteKey = "d") => {
      firestoreSubscriptions.push(onSnapshot(query, snapshot => {
        const changes = {[updateKey]: [], [deleteKey]: []};

        snapshot.docChanges().forEach(change => {
          let data = change.doc.data();

          if (data.payload) {
            changes[updateKey].push(data.payload);
          } else {
            changes[deleteKey].push(data.id);
          }
        });

        if (changes[updateKey].length > 0 || changes[deleteKey].length > 0) {
          dispatch(action(changes));
        }
      }));
    };

    firestoreSubscriptions.push(onSnapshot(makeQuery("projects"), snapshot => {
      snapshot.docChanges().forEach(change => {
        const data = change.doc.data().payload;

        if (data.locale !== locale || data.currencyMain !== currencyMain) {
          locale = data.locale;
          currencyMain = data.currencyMain;

          init();
        } else {
          dispatch(rxUpdateSettings(data));
        }
      });
    }));

    listenChanges(makeQuery("accounts"), rxUpdateAccounts);
    listenChanges(makeQuery("cars"), rxUpdateCars);

    listentUpdateDeleteChanges(makeQuery("budget_categories"), rxUpdateBudgetCategories);
    listentUpdateDeleteChanges(makeQuery("transactions"), rxUpdateTransactions, "utransactions", "dtransactions");
    listentUpdateDeleteChanges(makeQuery("mileage_logs"), rxUpdateMileageLogs, "umileageLogs", "dmileageLogs");
    listentUpdateDeleteChanges(makeQuery("portfolio_transactions"), rxUpdatePortfolioTransactions, "uportfolioTransactions", "dportfolioTransactions");
    listentUpdateDeleteChanges(makeQuery("quotes"), rxUpdateQuotes, "uquotes", "dquotes");
    listentUpdateDeleteChanges(makeQuery("tags"), rxUpdateTags);
    listentUpdateDeleteChanges(makeQuery("rules"), rxUpdateRules);
    listentUpdateDeleteChanges(makeQuery("categories"), rxUpdateCategories);

    syncQuotes();
  }, [dispatch, firestoreDb, syncQuotes, init]);

  useEffect(() => {
    const auth = getAuth();

    const unsub = onAuthStateChanged(auth, (user) => {
      if (user && !initialized) {
        const splash = document.getElementById("splash");

        if (splash) {
          splash.classList.remove("hidden");
        }

        init(user).then(context => {
          locale = context.locale;
          currencyMain = context.currencyMain;

          listenFirestoreChange(context.projectId);

          document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
              doSyncQuotes();
            }
          });

          const splash = document.getElementById("splash");

          if (splash) {
            splash.remove();
          }
        });
      } else if (!user && !signingOut) {
        navigate("/signin");
      }
    });

    return () => unsub();
  }, [navigate, listenFirestoreChange, initialized, init, doSyncQuotes, getAuth, onAuthStateChanged]);

  return {
    toggleLeftMenu,
    closeLeftMenuMobile,
    logout
  };
};

export default useApp;
